<template>
  <b-card
    no-body
    class="team-leave-balance-panel team-mid-panel h-100 mb-0"
    :class="{ 'team-mid-panel--mobile-collapsed': mobileCollapsed }"
  >
    <b-card-header
      class="team-mid-panel__header d-flex align-items-center justify-content-between py-50 px-1 border-bottom"
      :class="{ 'team-mid-panel__header--toggle': isNarrowViewport }"
      :role="isNarrowViewport ? 'button' : null"
      :tabindex="isNarrowViewport ? 0 : null"
      :aria-expanded="isNarrowViewport ? String(!mobileCollapsed) : null"
      @click="toggleMobileCollapsed"
      @keydown.enter.prevent="toggleMobileCollapsed"
      @keydown.space.prevent="toggleMobileCollapsed"
    >
      <h6 class="mb-0 d-flex align-items-center text-body team-mid-panel__title">
        <feather-icon icon="PieChartIcon" size="14" class="mr-50 text-primary" />
        Team Leave Balance
      </h6>
      <div class="d-flex align-items-center flex-shrink-0 team-mid-panel__header-actions">
        <b-button
          v-if="items.length > 0"
          size="sm"
          variant="outline-primary"
          class="team-leave-download-btn px-50 py-0"
          aria-label="Download"
          @click.stop="$emit('download')"
        >
          <feather-icon icon="DownloadIcon" size="12" />
        </b-button>
        <feather-icon
          v-if="isNarrowViewport"
          :icon="mobileCollapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'"
          size="16"
          class="text-muted team-mid-panel__collapse-chevron flex-shrink-0"
        />
      </div>
    </b-card-header>

    <b-card-body
      v-show="!isNarrowViewport || !mobileCollapsed"
      class="team-mid-panel__body p-1 d-flex flex-column"
      :aria-hidden="isNarrowViewport && mobileCollapsed ? 'true' : null"
    >
      <!-- Summary chips + search (search follows On leave) -->
      <div
        v-if="items.length > 0"
        class="team-leave-toolbar flex-shrink-0 mb-1"
      >
        <span class="team-leave-chip">
          <span class="team-leave-chip__label">Avg bal</span>
          <span class="team-leave-chip__value">{{ avgBalanceLabel }}</span>
        </span>
        <span
          class="team-leave-chip"
          :class="{ 'team-leave-chip--warn': zeroBalanceCount > 0 }"
        >
          <span class="team-leave-chip__label">Zero</span>
          <span class="team-leave-chip__value">{{ zeroBalanceCount }}</span>
        </span>
        <span
          v-if="onLeaveTodayCount != null"
          class="team-leave-chip"
        >
          <span class="team-leave-chip__label">On leave</span>
          <span class="team-leave-chip__value">{{ onLeaveTodayCount }}</span>
        </span>
        <div class="team-leave-search-wrap">
          <feather-icon icon="SearchIcon" size="12" class="team-leave-search-icon" />
          <b-form-input
            v-model="search"
            size="sm"
            placeholder="Search employee..."
            class="team-leave-search"
          />
        </div>
      </div>

      <div
        v-if="items.length === 0"
        class="d-flex align-items-center justify-content-center text-muted small flex-grow-1 py-1"
      >
        <feather-icon icon="UsersIcon" size="14" class="mr-50" />
        <span>No leave balance data</span>
      </div>

      <div
        v-else-if="filteredItems.length === 0"
        class="d-flex align-items-center justify-content-center text-muted small flex-grow-1 py-1"
      >
        <span>No matches</span>
      </div>

      <div
        v-else
        class="team-mid-table team-leave-table-wrap"
      >
        <div class="team-mid-table__scroll team-mid-list">
          <div class="team-mid-table__header team-mid-table__grid--leave team-leave-header-row px-50">
            <span class="team-leave-col-name team-mid-table__th team-leave-th">Employee</span>
            <span class="team-leave-col-num team-mid-table__th team-leave-th">CL</span>
            <span class="team-leave-col-num team-mid-table__th team-leave-th">SL</span>
            <span class="team-leave-col-num team-mid-table__th team-leave-th">PL</span>
            <span class="team-leave-col-bal team-mid-table__th team-leave-th">Bal</span>
            <span class="team-leave-col-taken team-mid-table__th team-leave-th">Taken</span>
            <span class="team-leave-col-pend team-mid-table__th team-leave-th">Pend</span>
            <span class="team-leave-col-onleave team-mid-table__th team-leave-th">Today</span>
          </div>
          <b-list-group flush class="team-mid-table__list team-leave-list">
            <b-list-group-item
              v-for="row in pagedRows"
              :key="row.emp_id"
              class="px-50 team-mid-table__row team-mid-table__grid--leave team-leave-row"
              role="button"
              @click="openBreakup(row)"
            >
              <span class="team-leave-col-name d-flex align-items-center min-w-0">
                <span
                  class="small font-weight-bolder text-truncate team-mid-table__name"
                >
                  {{ getUserName(row.emp_id) }}
                </span>
                <span
                  v-if="isLowBalance(row)"
                  class="team-leave-low-hint ml-25"
                >low</span>
              </span>
              <span class="team-leave-col-num team-leave-num">
                {{ formatTypeBal(typeRemaining(row, 'cl')) }}
              </span>
              <span class="team-leave-col-num team-leave-num">
                {{ formatTypeBal(typeRemaining(row, 'sl')) }}
              </span>
              <span class="team-leave-col-num team-leave-num">
                {{ formatTypeBal(typeRemaining(row, 'pl')) }}
              </span>
              <span class="team-leave-col-bal">
                <b-badge
                  :variant="isZeroBalance(row) ? 'light-secondary' : (isLowBalance(row) ? 'warning' : 'success')"
                  class="team-leave-bal-badge"
                >
                  {{ roundToHalf(row.leaveBalance) }}
                </b-badge>
              </span>
              <span class="small text-nowrap team-leave-col-taken team-leave-taken">
                {{ roundToHalf(row.leavesApplied) }}
              </span>
              <span class="team-leave-col-pend team-leave-num">
                {{ formatPending(row) }}
              </span>
              <span class="team-leave-col-onleave">
                <b-badge
                  v-if="isOnLeaveToday(row)"
                  class="team-leave-onleave-badge"
                >Yes</b-badge>
                <span v-else class="team-leave-onleave-empty">—</span>
              </span>
            </b-list-group-item>
          </b-list-group>
        </div>
        <div
          v-if="filteredItems.length > 0"
          class="team-mid-table__pager d-flex align-items-center justify-content-between flex-wrap px-50 py-25"
        >
          <span class="team-mid-table__pager-meta text-muted">
            Showing {{ pageRangeStart }}–{{ pageRangeEnd }} of {{ filteredItems.length }}
          </span>
          <b-pagination
            v-if="filteredItems.length > pageSize"
            v-model="currentPage"
            :total-rows="filteredItems.length"
            :per-page="pageSize"
            size="sm"
            pills
            align="right"
            class="mb-0 team-mid-table__pagination"
            :hide-goto-end-buttons="true"
            limit="5"
          />
        </div>
      </div>
    </b-card-body>

    <!-- Compact leave breakup (click employee name) -->
    <b-modal
      v-model="breakupVisible"
      title="Leaves - Breakup"
      hide-footer
      size="md"
      scrollable
      modal-class="leave-breakup-modal"
      header-class="leave-breakup-header"
      body-class="leave-breakup-body"
      content-class="leave-breakup-content"
    >
      <div v-if="selected" class="leave-breakup">
        <div class="leave-breakup-employee">
          <feather-icon icon="UserIcon" size="16" class="leave-breakup-employee-icon" />
          <span class="leave-breakup-employee-name">{{ getUserName(selected.emp_id) }}</span>
        </div>

        <div class="leave-breakup-totals">
          <div class="leave-breakup-total-row">
            <span class="leave-breakup-label">Leave balance</span>
            <b-badge class="leave-breakup-badge leave-breakup-badge--bal">
              {{ roundToHalf(selected.leaveBalance) }} days
            </b-badge>
          </div>
          <div class="leave-breakup-total-row">
            <span class="leave-breakup-label">Leaves taken / applied</span>
            <b-badge class="leave-breakup-badge leave-breakup-badge--taken">
              {{ roundToHalf(selected.leavesApplied) }} days
            </b-badge>
          </div>
        </div>

        <b-table
          v-if="breakupRows.length > 0"
          :items="breakupRows"
          :fields="breakupFields"
          small
          bordered
          hover
          thead-class="leave-breakup-thead"
          class="mb-0 leave-breakup-table"
        >
          <template #cell(available)="row">
            <span
              class="leave-breakup-bal"
              :class="Number(row.item.available) > 0 ? 'leave-breakup-bal--ok' : 'leave-breakup-bal--zero'"
            >
              {{ roundToHalf(row.item.available) }}
            </span>
          </template>
          <template #cell(used)="row">
            <span class="leave-breakup-taken-cell">{{ roundToHalf(row.item.used) }}</span>
          </template>
        </b-table>
        <p v-else class="leave-breakup-empty mb-0">No type breakup available</p>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BBadge,
  BButton,
  BFormInput,
  BListGroup,
  BListGroupItem,
  BModal,
  BTable,
  BPagination,
} from 'bootstrap-vue'

const PAGE_SIZE = 10
/** Same threshold as employeesWithLowLeaveBalance in computedMixin */
const LOW_BALANCE_THRESHOLD = 5

const TYPE_MATCHERS = {
  cl: ['casual'],
  sl: ['sick', 'medical'],
  // privilege / pto only — avoid matching paternity
  pl: ['privilege', 'pto'],
}

export default {
  name: 'TeamLeaveBalancePanel',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
    BButton,
    BFormInput,
    BListGroup,
    BListGroupItem,
    BModal,
    BTable,
    BPagination,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    getUserName: {
      type: Function,
      required: true,
    },
    roundToHalf: {
      type: Function,
      required: true,
    },
    /** Approved non-WFH leave count today — from existing roster leave logic */
    onLeaveTodayCount: {
      type: Number,
      default: null,
    },
    /** Emp IDs on approved non-WFH leave today (from teamTodayAttendanceRoster) */
    onLeaveTodayIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const narrow = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 991.98px)').matches
      : false
    return {
      search: '',
      currentPage: 1,
      pageSize: PAGE_SIZE,
      breakupVisible: false,
      selected: null,
      breakupFields: [
        { key: 'label', label: 'Type', thClass: 'leave-breakup-th', tdClass: 'leave-breakup-td' },
        { key: 'available', label: 'Bal', thClass: 'leave-breakup-th text-right', tdClass: 'leave-breakup-td text-right' },
        { key: 'used', label: 'Taken', thClass: 'leave-breakup-th text-right', tdClass: 'leave-breakup-td text-right' },
      ],
      /** Phone/tablet (≤991): stays collapsed until user toggles — match Org Snapshot */
      mobileCollapsed: true,
      isNarrowViewport: narrow,
    }
  },
  mounted() {
    if (typeof window === 'undefined' || !window.matchMedia) return
    this._panelMq = window.matchMedia('(max-width: 991.98px)')
    this._onPanelMq = e => {
      const nowNarrow = e.matches
      if (nowNarrow && !this.isNarrowViewport) {
        this.mobileCollapsed = true
      }
      this.isNarrowViewport = nowNarrow
    }
    this.isNarrowViewport = this._panelMq.matches
    if (this.isNarrowViewport) {
      this.mobileCollapsed = true
    }
    if (this._panelMq.addEventListener) {
      this._panelMq.addEventListener('change', this._onPanelMq)
    } else if (this._panelMq.addListener) {
      this._panelMq.addListener(this._onPanelMq)
    }
  },
  beforeDestroy() {
    if (!this._panelMq || !this._onPanelMq) return
    if (this._panelMq.removeEventListener) {
      this._panelMq.removeEventListener('change', this._onPanelMq)
    } else if (this._panelMq.removeListener) {
      this._panelMq.removeListener(this._onPanelMq)
    }
  },
  computed: {
    onLeaveTodaySet() {
      return new Set((this.onLeaveTodayIds || []).map(id => String(id)))
    },
    filteredItems() {
      if (!this.items || this.items.length === 0) return []
      const q = (this.search || '').trim().toLowerCase()
      if (!q) return this.items
      return this.items.filter(item => {
        const name = (this.getUserName(item.emp_id) || item.emp_id || '').toLowerCase()
        return name.includes(q)
      })
    },
    pageRangeStart() {
      if (this.filteredItems.length === 0) return 0
      return (this.currentPage - 1) * this.pageSize + 1
    },
    pageRangeEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredItems.length)
    },
    pagedRows() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredItems.slice(start, start + this.pageSize)
    },
    avgBalanceLabel() {
      if (!this.items || this.items.length === 0) return '—'
      const sum = this.items.reduce((acc, row) => acc + (parseFloat(row.leaveBalance) || 0), 0)
      const avg = sum / this.items.length
      return this.roundToHalf(avg)
    },
    zeroBalanceCount() {
      return (this.items || []).filter(row => this.isZeroBalance(row)).length
    },
    breakupRows() {
      if (!this.selected || !Array.isArray(this.selected.breakdown)) return []
      return this.selected.breakdown
        .map(item => ({
          label: item.label || item.type || '-',
          available: parseFloat(item.available || item.value || 0) || 0,
          used: parseFloat(item.used || item.Used || item.used_days || 0) || 0,
        }))
        .filter(item => item.available > 0 || item.used > 0)
    },
  },
  watch: {
    search() {
      this.currentPage = 1
    },
    filteredItems(rows) {
      const totalPages = Math.max(1, Math.ceil((rows || []).length / this.pageSize))
      if (this.currentPage > totalPages) this.currentPage = totalPages
    },
  },
  methods: {
    toggleMobileCollapsed() {
      if (!this.isNarrowViewport) return
      this.mobileCollapsed = !this.mobileCollapsed
    },
    balValue(row) {
      return parseFloat(row && row.leaveBalance) || 0
    },
    isZeroBalance(row) {
      return this.balValue(row) <= 0
    },
    isLowBalance(row) {
      const bal = this.balValue(row)
      return bal > 0 && bal < LOW_BALANCE_THRESHOLD
    },
    isOnLeaveToday(row) {
      if (!row || row.emp_id == null) return false
      return this.onLeaveTodaySet.has(String(row.emp_id))
    },
    /**
     * Remaining for CL / SL / PL from existing breakup rows.
     * Returns null when that type is absent from breakdown (show "—").
     */
    typeRemaining(row, key) {
      if (row && row[key] != null && row[key] !== '') {
        const n = parseFloat(row[key])
        return Number.isNaN(n) ? null : n
      }
      if (!row || !Array.isArray(row.breakdown) || row.breakdown.length === 0) return null
      const matchers = TYPE_MATCHERS[key] || []
      const item = row.breakdown.find(b => {
        const type = String(b.type || '').toLowerCase()
        const label = String(b.label || '').toLowerCase()
        return matchers.some(m => type.includes(m) || label.includes(m))
      })
      if (!item) return null
      const n = parseFloat(item.available != null ? item.available : item.value)
      return Number.isNaN(n) ? null : n
    },
    formatTypeBal(value) {
      if (value == null) return '—'
      return this.roundToHalf(value)
    },
    formatPending(row) {
      if (!row) return '—'
      if (row.pendingCount == null && row.pending == null) return '—'
      const n = parseFloat(row.pendingCount != null ? row.pendingCount : row.pending)
      if (Number.isNaN(n)) return '—'
      if (n <= 0) return '—'
      return String(Math.round(n))
    },
    openBreakup(row) {
      this.selected = row
      this.breakupVisible = true
    },
  },
}
</script>

<style src="./TeamMidTable.css"></style>

<style lang="scss" scoped>
.team-leave-balance-panel {
  border: 1px solid #e8ecf0;
  box-shadow: none;
  border-radius: 10px;
  overflow: hidden;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.team-mid-panel__header-actions {
  gap: 0.4rem;
  margin-left: auto;
}

.team-leave-balance-panel :deep(.card-header) {
  background: #fff;
  flex-shrink: 0;
}

.team-leave-balance-panel :deep(.card-header) h6 {
  font-size: 0.8rem;
  font-weight: 600;
}

.team-leave-download-btn {
  min-height: 22px;
  line-height: 1;
}

.team-leave-balance-panel :deep(.card-body) {
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.team-leave-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.28rem;
}

.team-leave-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  padding: 0.14rem 0.4rem;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  line-height: 1.2;
}

.team-leave-chip--warn {
  border-color: #fde68a;
  background: #fffbeb;
}

.team-leave-chip__label {
  font-size: 0.55rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9ca3af;
}

.team-leave-chip--warn .team-leave-chip__label {
  color: #b45309;
}

.team-leave-chip__value {
  font-size: 0.68rem;
  font-weight: 700;
  color: #0f766e;
  font-variant-numeric: tabular-nums;
}

.team-leave-chip--warn .team-leave-chip__value {
  color: #b45309;
}

.team-leave-search-wrap {
  position: relative;
  flex: 1 1 7rem;
  min-width: 7rem;
  max-width: 100%;
}

@media (max-width: 991px) {
  .team-leave-toolbar {
    width: 100%;
  }

  .team-leave-search-wrap {
    flex: 1 1 100%;
    min-width: 0;
    width: 100%;
  }
}

.team-leave-search-icon {
  position: absolute;
  left: 0.45rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  z-index: 1;
}

.team-leave-search {
  font-size: 0.7rem;
  padding-left: 1.55rem !important;
  min-height: 1.7rem;
  border-color: #e5e7eb;
}

.team-leave-search:focus {
  border-color: #99f6e4;
  box-shadow: 0 0 0 0.15rem rgba(13, 148, 136, 0.12);
}

/* Table chrome/header/rows/name/scroll: shared via TeamMidTable.css (.team-mid-table) */

.team-leave-row {
  cursor: pointer;
}

.team-leave-col-name {
  min-width: 0;
  font-size: 0.72rem;
}

.min-w-0 {
  min-width: 0;
}

.team-leave-low-hint {
  flex-shrink: 0;
  font-size: 0.52rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #d97706;
  opacity: 0.85;
  line-height: 1;
}

.team-leave-col-num,
.team-leave-col-bal,
.team-leave-col-taken,
.team-leave-col-pend,
.team-leave-col-onleave {
  white-space: nowrap;
  text-align: center;
  justify-self: center;
}

.team-leave-header-row .team-leave-col-name {
  text-align: left;
}

.team-leave-num {
  font-size: 0.68rem;
  font-weight: 600;
  color: #374151;
  font-variant-numeric: tabular-nums;
}

.team-leave-bal-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.7rem;
  font-size: 0.6rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.team-leave-taken {
  font-weight: 600;
  font-size: 0.7rem;
  color: #4b5563;
  font-variant-numeric: tabular-nums;
}

.team-leave-onleave-badge {
  font-size: 0.5rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0.1rem 0.28rem;
  border-radius: 4px;
  line-height: 1.15;
  background: #ecfdf5;
  color: #0f766e;
  border: 1px solid #99f6e4;
}

.team-leave-onleave-empty {
  font-size: 0.68rem;
  font-weight: 500;
  color: #c4c9d2;
}

@media (max-width: 991px) {
  .team-mid-panel__header--toggle {
    cursor: pointer;
    user-select: none;
    min-height: 2.5rem;
    gap: 0.5rem;
  }

  .team-mid-panel__header--toggle:hover {
    background: #f8fafb;
  }

  .team-mid-panel__header--toggle:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(0, 172, 153, 0.25);
  }

  .team-mid-panel__collapse-chevron {
    margin-left: 0.15rem;
  }

  .team-mid-panel--mobile-collapsed {
    height: auto !important;
    min-height: 0 !important;
  }

  .team-leave-balance-panel.team-mid-panel--mobile-collapsed > .card-body,
  .team-leave-balance-panel.team-mid-panel--mobile-collapsed .team-mid-panel__body {
    display: none !important;
  }
}
</style>

<!-- Modal is teleported to body — unscoped so classes apply -->
<style lang="scss">
.leave-breakup-modal {
  .modal-dialog {
    max-width: 420px;
  }

  .leave-breakup-content {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    overflow: hidden;
  }

  .leave-breakup-header {
    display: flex;
    align-items: center;
    background: #f8fafb;
    border-bottom: 1px solid #e5e7eb !important;
    padding: 0.65rem 0.9rem !important;

    .modal-title {
      font-size: 0.88rem;
      font-weight: 700;
      color: #1f2937;
      letter-spacing: 0.01em;
      line-height: 1.2;
    }

    .close {
      margin: -0.25rem -0.15rem -0.25rem auto;
      padding: 0.35rem 0.45rem;
      font-size: 1.15rem;
      font-weight: 500;
      line-height: 1;
      color: #6b7280;
      opacity: 1;
      text-shadow: none;
      border-radius: 6px;
      transition: background 0.15s ease, color 0.15s ease;

      &:hover {
        background: #eef2f5;
        color: #111827;
        opacity: 1;
      }
    }
  }

  .leave-breakup-body {
    padding: 0.85rem 0.95rem 0.9rem !important;
    background: #fff;
  }
}

.leave-breakup {
  .leave-breakup-employee {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-bottom: 0.55rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e8ecf0;
  }

  .leave-breakup-employee-icon {
    color: #0f766e;
    flex-shrink: 0;
  }

  .leave-breakup-employee-name {
    font-size: 0.95rem;
    font-weight: 700;
    color: #111827;
    line-height: 1.25;
    letter-spacing: -0.01em;
  }

  .leave-breakup-totals {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-bottom: 0.7rem;
  }

  .leave-breakup-total-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .leave-breakup-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #4b5563;
  }

  .leave-breakup-badge {
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    padding: 0.22rem 0.55rem;
    border-radius: 999px;
    border: 1px solid transparent;
    line-height: 1.2;
  }

  .leave-breakup-badge--bal {
    background: #f0fdfa;
    color: #0f766e;
    border-color: #99f6e4;
  }

  .leave-breakup-badge--taken {
    background: #f8fafc;
    color: #475569;
    border-color: #cbd5e1;
  }

  .leave-breakup-table {
    font-size: 0.78rem;
    border-color: #e5e7eb !important;

    th,
    td {
      padding: 0.35rem 0.5rem !important;
      vertical-align: middle;
      border-color: #e8ecf0 !important;
    }

    .leave-breakup-th {
      font-size: 0.7rem;
      font-weight: 700;
      color: #374151 !important;
      background: #f3f4f6 !important;
      text-transform: none;
      white-space: nowrap;
    }

    .leave-breakup-td {
      color: #1f2937;
      font-weight: 500;
    }

    tbody tr:nth-child(even) {
      background: #fafbfc;
    }
  }

  .leave-breakup-bal {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .leave-breakup-bal--ok {
    color: #0f766e;
  }

  .leave-breakup-bal--zero {
    color: #9ca3af;
    font-weight: 600;
  }

  .leave-breakup-taken-cell {
    color: #475569;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .leave-breakup-empty {
    font-size: 0.78rem;
    font-weight: 500;
    color: #6b7280;
    padding-top: 0.15rem;
  }
}
</style>
